import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { PLAYBOOKS_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import usePreventLeavePage from "app/pages/sales-intelligence/hooks/usePreventLeavePage";

import { DEFAULT_PLAYBOOK_CONTACTS_COUNT } from "../constants";
import type { Playbook } from "../types/playbooks.types";
import type { PlaybookWizardMode } from "../types/playbookWizard.types";
import { hasPlaybookWizardChanges } from "../utils/playbookWizardHasChanges";

import { usePlaybooksTrackingService } from "./usePlaybooksTrackingService";
import { PLAYBOOK_STEP_INDEX, PLAYBOOK_WIZARD_STEPS, type PlaybookWizardNavigation } from "./usePlaybookWizard";
import type { PlaybookWizardState } from "./usePlaybookWizardState";

type UsePlaybookWizardFlowParams = {
    mode: PlaybookWizardMode;
    playbook?: Playbook;
    onDone: () => void;
    state: PlaybookWizardState;
    wizard: PlaybookWizardNavigation;
};

export const usePlaybookWizardFlow = ({ mode, playbook, onDone, state, wizard }: UsePlaybookWizardFlowParams) => {
    const { translate } = useTranslate();
    const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
    const playbooksTracking = usePlaybooksTrackingService();

    const {
        name,
        description,
        selectedCadenceId,
        scoringRuleId,
        enabledProfileIds,
        contactsCount,
        employeeLocation,
        setContactsCount,
        submit,
        isSubmitting,
    } = state;

    const hasChanges = hasPlaybookWizardChanges({
        playbook,
        name,
        description,
        selectedCadenceId,
        scoringRuleId,
        enabledProfileIds,
        contactsCount,
        employeeLocation,
    });

    const { setConfirm: confirmLeavePage, resetRedirectToState } = usePreventLeavePage(
        hasChanges,
        () => setIsUnsavedChangesModalOpen(true),
        [],
        { name: PLAYBOOKS_ROUTE, params: {} },
    );

    const confirmLeaveAfterSave = () => {
        resetRedirectToState();
        confirmLeavePage(true);
    };

    const handlePrimaryClick = () => {
        const stepName = PLAYBOOK_WIZARD_STEPS[wizard.activeStep];
        if (wizard.isLastStep) {
            playbooksTracking.trackSubmit(mode === "edit" ? "save" : "create");
            void submit(confirmLeaveAfterSave);
            return;
        }

        playbooksTracking.trackNext(stepName);

        if (wizard.activeStep === PLAYBOOK_STEP_INDEX.employeeProfiles && contactsCount === undefined) {
            setContactsCount(DEFAULT_PLAYBOOK_CONTACTS_COUNT);
        }

        wizard.goNext();
    };

    const primaryLabel = wizard.isLastStep
        ? mode === "edit"
            ? translate("si.common.button.save")
            : translate("si.playbooks.wizard.button.create")
        : translate("common.next");

    const handleCancelClick = () => {
        playbooksTracking.trackCancel();
        if (hasChanges) {
            setIsUnsavedChangesModalOpen(true);
            return;
        }

        onDone();
    };

    const handleConfirmLeave = () => {
        playbooksTracking.trackUnsavedLeave();
        setIsUnsavedChangesModalOpen(false);
        confirmLeavePage(true);
    };

    const handleStayOnPage = () => {
        playbooksTracking.trackUnsavedStay();
        setIsUnsavedChangesModalOpen(false);
        resetRedirectToState();
    };

    const handleBack = () => {
        const stepName = PLAYBOOK_WIZARD_STEPS[wizard.activeStep];
        playbooksTracking.trackBack(stepName);
        wizard.goBack();
    };

    return {
        primaryLabel,
        isSubmitting,
        handlePrimaryClick,
        handleCancelClick,
        handleBack,
        isUnsavedChangesModalOpen,
        handleConfirmLeave,
        handleStayOnPage,
    };
};
